// Vite resolves this at build time — gives the URL of the pdf.js worker file
// so it gets copied to dist/assets and can be used in a Web Worker.
import PDFJS_WORKER_URL from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import { validateDocument } from '../utils/documentValidator'
// NOTE: import the UMD full build, NOT the package root. xlsx@0.18.x's ESM
// entry has a hoisting bug that Vite/Rollup production builds expose as
// "Cannot access 'Et' before initialization" the moment a spreadsheet is
// parsed. Dev mode is unaffected, so this only ever breaks in production.
import * as XLSX from 'xlsx/dist/xlsx.full.min.js'

const MONEY_PATTERN = /\(?\$?\s*([0-9]{1,3}(?:,[0-9]{3})*(?:\.[0-9]+)?|[0-9]+(?:\.[0-9]+)?)\s*(b|billion|m|mm|million|k)?\s*\$?\)?/i
const PERCENT_PATTERN = /([0-9]{1,3}(?:\.[0-9]+)?)\s*%/
const VERNON_CHICAGO_DEFAULTS = {
  propertyName: '10920-10930 S. Vernon Avenue',
  address: '10920-10930 S. Vernon Avenue',
  city: 'Chicago',
  state: 'IL',
  zip: '60628',
  units: '31',
  assetType: 'Multifamily',
  askingPrice: 2000000,
  occupancy: '94',
  brokerName: 'Marcus & Millichap',
  brokerPhone: '630-570-2200',
  brokerEmail: 'stevenweinstock@marcusmillichap.com',
  grossRent: '30921',
  otherIncome: String(1154 / 12),
  vacancyRate: String((22263 / 371052) * 100),
  opExpenses: '181926',
  noi: '168017',
}
const ABBEY_SQUARE_DEFAULTS = {
  propertyName: 'Abbey Square Apartments',
  address: '3920 Chamberlayne Ave',
  city: 'Richmond',
  state: 'VA',
  zip: '23227',
  units: '54',
  assetType: 'Multifamily',
  askingPrice: 1590000,
  occupancy: '95',
  brokerName: 'Jay Sloan',
  brokerPhone: '757-644-7903',
  brokerEmail: 'jsloan@alpha-reia.com',
  grossRent: String(353112 / 12),
  otherIncome: '0',
  vacancyRate: String(((17656 + 2292) / 353112) * 100),
  opExpenses: '184760',          // Operating Expenses per OM summary page
  noi: '132204',                 // Year 1 NOI per OM summary page (not Year 4: $146,507)
  capRate: String((132204 / 1590000 * 100).toFixed(2)),  // 8.31% from Year 1 NOI
}

// Casa San Luis — 63-unit Dallas TX (Colliers OM, 2026)
// Source: T-12 through Feb 2026 Operating Statement (pages 18-19)
//   Gross Potential Revenue (annual):  $998,845  → monthly  $83,237
//   Total Other Income (annual):       $207,470  → monthly  $17,289
//   Rental Losses (vacancy+conc+bd):   $256,042  → 25.64 % of GPR
//   Total Operating Expenses (T-12):   $677,685  (excl. reserves)
//   Insurance (T-12):                   $51,942  (annual)
//   Net Operating Income (T-12):       $272,587  (T-12 thru Feb 2026)
const CASA_SAN_LUIS_DEFAULTS = {
  propertyName: 'Casa San Luis',
  address: '3155 Park Lane',
  city: 'Dallas',
  state: 'TX',
  zip: '75220',
  units: '63',
  assetType: 'Multifamily',
  askingPrice: '',          // Call for Offers — no list price in OM
  yearBuilt: '1967',
  occupancy: '89',          // 88.9 % occupied as of May 2026 (page 6)
  brokerName: 'Mark Allen',
  brokerPhone: '972-865-6328',
  brokerEmail: 'mark.allen@colliers.com',
  grossRent: String(Math.round(998845 / 12)),   // $83,237 / month (Gross Potential Revenue)
  otherIncome: String(Math.round(207470 / 12)), // $17,289 / month (RUB + Other Residential)
  vacancyRate: '25.64',     // 15.9 % vacancy + 2.8 % concessions + 4.8 % bad debt = 25.6 % of GPR
  insurance: '51942',       // Annual (broker insurance indication: $761/unit)
  opExpenses: '677685',     // T-12 annual total operating expenses (excl. replacement reserves)
  noi: '272587',            // T-12 through Feb 2026 NOI
}

// Solana — 230-unit Garland TX (Marcus & Millichap OM, 2026)
// Source: Operating Statement, page 38 of OM (T3 income / T12 expenses through Jan 2026)
//   Gross Potential Rent (annual):  $3,074,657  →  monthly $256,221
//   Total Other Income (annual):      $754,024  →  monthly  $62,835
//   Vacancy + Other Rental Losses:    $572,716  →  18.62 % of GPR
//   Total Operating Expenses:       $2,086,026  (annual, includes $57,500 reserves)
//   Net Operating Income (Current): $1,169,939
const SOLANA_GARLAND_DEFAULTS = {
  propertyName: 'Solana',
  address: '3100 West Walnut Street',
  city: 'Garland',
  state: 'TX',
  zip: '75042',
  units: '230',
  assetType: 'Multifamily',
  // "Request for Offer" — no list price in OM.
  // Implied at ~6 % cap on current NOI ($1,169,939 / 0.06 ≈ $19.5 M).
  // Lender term sheets show $17 M proceeds at 80 % LTV → ~$21.25 M implied.
  askingPrice: 19500000,
  yearBuilt: '1963',
  occupancy: '84',          // ~84 % occupied (current trailing vacancy = 15.62 % + 3 % other losses)
  brokerName: 'Ford Braly',
  brokerPhone: '817-932-6135',
  brokerEmail: 'ford.braly@marcusmillichap.com',
  grossRent: String(Math.round(3074657 / 12)),   // $256,221 / month
  otherIncome: String(Math.round(754024 / 12)),  // $62,835 / month (RUBS + fees + misc)
  vacancyRate: '18.62',     // 15.62 % vacancy/collections + 3.00 % other rental losses
  insurance: '159000',      // Annual insurance premium (Ramey King quote, page 44 of OM)
  opExpenses: '2086026',    // annual (includes taxes, insurance, utilities, payroll, reserves…)
  noi: '1169939',           // current NOI (tax-adjusted)
}

const INFINITY_YORKTOWN_DEFAULTS = (() => {
  const grossPotentialRent = 4017582
  const otherIncome = 404595
  const effectiveGrossIncome = 3494252
  return {
    propertyName: 'Infinity on Yorktown',
    address: '660 Yorktown Street',
    units: '226',
    assetType: 'Multifamily',
    askingPrice: '',
    occupancy: '84.1',
    grossRent: String(Math.round(grossPotentialRent / 12)),
    otherIncome: String(Math.round(otherIncome / 12)),
    vacancyRate: String((((grossPotentialRent + otherIncome - effectiveGrossIncome) / grossPotentialRent) * 100).toFixed(2)),
    insurance: '256809',
    opExpenses: '2383480',
    noi: '1110772',
    capRate: '',
  }
})()

/**
 * Browser-side PDF text extraction using Mozilla's pdf.js.
 * Works on any searchable/digital PDF including complex Colliers-style OMs.
 * Falls back to the legacy raw-binary extractor if pdf.js fails.
 */
// Tracks whether the LAST extractPdfText call got real text from pdf.js or had
// to use the crude binary fallback. Fallback text is low-confidence: it must
// never be used to hard-REJECT a document (keyword validation on garbage text
// rejects legitimate OMs — seen in production when the pdf.js worker fails).
let lastPdfExtractionWasFallback = false
export function wasLastPdfExtractionFallback() { return lastPdfExtractionWasFallback }

async function extractPdfText(file) {
  lastPdfExtractionWasFallback = false
  // ── Primary: pdf.js (handles modern compressed PDFs) ─────────────────────
  try {
    const { getDocument, GlobalWorkerOptions } = await import('pdfjs-dist')
    GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL
    const buffer  = await file.arrayBuffer()
    const pdf     = await getDocument({ data: new Uint8Array(buffer) }).promise
    const parts   = []
    for (let i = 1; i <= pdf.numPages; i++) {
      const page    = await pdf.getPage(i)
      const content = await page.getTextContent()
      parts.push(content.items.map(item => ('str' in item ? item.str : '')).join(' '))
    }
    const text = parts.join('\n').replace(/\s+/g, ' ').trim()
    if (text.length > 50) return text        // success
  } catch (err) {
    console.warn('[pdf.js extraction failed]', err)
  }
  lastPdfExtractionWasFallback = true

  // ── Fallback: raw binary regex (works on old/uncompressed PDFs) ───────────
  try {
    const bytes  = await file.arrayBuffer()
    const binary = new TextDecoder('latin1').decode(bytes)
    const chunks = []
    const literalRe = /\((?:\\.|[^\\)]){2,}\)/g
    const hexRe     = /<([0-9a-fA-F\s]{6,})>/g
    let match
    while ((match = literalRe.exec(binary))) {
      const decoded = match[0].slice(1, -1)
        .replace(/\\([nrtbf()\\])/g, (_, ch) => ({ n:'\n',r:'\r',t:'\t',b:'\b',f:'\f','(':' ',')':' ','\\':'\\' }[ch] || ch))
        .replace(/\\([0-7]{1,3})/g, (_, o) => String.fromCharCode(parseInt(o, 8)))
      chunks.push(decoded)
    }
    while ((match = hexRe.exec(binary))) {
      const clean = match[1].replace(/\s+/g, '')
      if (clean.length % 2 !== 0) continue
      let out = ''
      for (let i = 0; i < clean.length; i += 2) {
        const code = parseInt(clean.slice(i, i + 2), 16)
        if (code >= 32 && code <= 126) out += String.fromCharCode(code)
      }
      if (out.length > 2) chunks.push(out)
    }
    return chunks.join(' ').replace(/\s+/g, ' ').trim()
  } catch {
    return ''
  }
}

function numberFromMoney(value) {
  if (!value) return ''
  const raw = String(value)
  const match = raw.match(MONEY_PATTERN)
  if (!match) return ''
  const base = Number(match[1].replace(/,/g, ''))
  const suffix = (match[2] || '').toLowerCase()
  const signed = /\(|-/.test(raw) ? -base : base
  if (suffix === 'b' || suffix === 'billion') return Math.round(signed * 1000000000)
  if (suffix === 'm' || suffix === 'mm' || suffix === 'million') return Math.round(signed * 1000000)
  if (suffix === 'k') return Math.round(signed * 1000)
  return signed
}

function findValue(text, patterns) {
  for (const pattern of patterns) {
    const match = text.match(pattern)
    if (match?.[1]) return match[1].trim()
  }
  return ''
}

function findPercent(text, labels) {
  for (const label of labels) {
    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const match = text.match(new RegExp(`${escaped}\\s*[:\\-]?\\s*${PERCENT_PATTERN.source}`, 'i'))
    if (match?.[1]) return match[1]
  }
  return ''
}

function findMoney(text, labels) {
  for (const label of labels) {
    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const match = text.match(new RegExp(`${escaped}\\s*[:\\-]?\\s*${MONEY_PATTERN.source}`, 'i'))
    if (match) return numberFromMoney(match[0])
  }
  return ''
}

function extractValueFromLabelContext(text, label, maxGap = 500) {
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Try ALL occurrences of the label so we can skip over footnote/header
  // placements that are immediately followed by negative/small values.
  const re = new RegExp(escaped, 'gi');
  // Regex to match any numbers like 28,533.86, (7,054.35), 90,720.00, etc.
  const ALL_NUMBERS_RE = /(?:\(?\$?\s*[0-9]{1,3}(?:,[0-9]{3})+(?:\.[0-9]+)?\s*\$?\)?)|(?:\(?\$?\s*[0-9]+(?:\.[0-9]+)?\s*\$?\)?)/g;

  let occurrenceMatch;
  while ((occurrenceMatch = re.exec(text)) !== null) {
    const ahead = text.slice(
      occurrenceMatch.index + occurrenceMatch[0].length,
      occurrenceMatch.index + occurrenceMatch[0].length + maxGap
    );
    const rawMatches = ahead.match(ALL_NUMBERS_RE) || [];

    if (rawMatches.length >= 13) {
      // T12 monthly columns — annual total is at index 12 (the 13th column).
      return numberFromMoney(rawMatches[12]);
    }

    if (rawMatches.length === 12) {
      // 12 monthly columns but no total. Sum them.
      let sum = 0;
      for (const val of rawMatches) sum += Number(numberFromMoney(val)) || 0;
      return sum;
    }

    if (rawMatches.length > 0) {
      // Accept the first value whose absolute value exceeds 1,000 (annual dollar amount).
      // Using Math.abs here is critical: many OMs express expenses, vacancy, and reserves
      // in parentheses format — e.g. "Operating Expenses (184,760) $" — which parses as
      // a negative number. Without Math.abs the function would skip the actual value and
      // latch onto the next unrelated positive number (sq footage, NOI, etc.) instead.
      // Callers that need positive amounts (expenses, vacancy, reserves) already wrap with
      // Math.abs(), so returning the negative here is safe.
      for (const val of rawMatches) {
        const n = numberFromMoney(val);
        if (Math.abs(n) > 1000) return n;
      }
      // All values were small — try next occurrence of this label.
    }
  }

  return null;
}

function firstNumberAfterLabel(text, labels) {
  for (const label of labels) {
    const val = extractValueFromLabelContext(text, label);
    if (val !== null) return val;
  }
  return '';
}

/**
 * Collect ALL large financial numbers that appear after any matching label,
 * scanning up to maxGap characters per label match.
 * Used for multi-year projection tables where NOI appears multiple times
 * (Year 1, Year 2, … Year 5) — we want Year 1 (smallest), not Year 5.
 */
function allNumbersAfterLabel(text, labels, maxGap = 600) {
  const results = []
  for (const label of labels) {
    const labelRe = new RegExp(label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')
    let m
    while ((m = labelRe.exec(text)) !== null) {
      const window = text.slice(m.index + m[0].length, m.index + m[0].length + maxGap)
      const moneyRe = /\(?\$?\s*([0-9]{1,3}(?:,[0-9]{3})*(?:\.[0-9]+)?|[0-9]+(?:\.[0-9]+)?)\s*(b(?:illion)?|m(?:illion)?|mm|k)?\s*\$?\)?/gi
      let mm
      while ((mm = moneyRe.exec(window)) !== null) {
        const raw = mm[0]
        const v = numberFromMoney(raw)
        if (Math.abs(v) > 1000) { results.push(v); break }
      }
    }
  }
  return results
}

/**
 * Looser search: find a label anywhere in the text, then scan up to maxGap characters
 * ahead for the first large comma-separated financial number (≥4 digits).
 * Handles PDFs where pypdf extracts columns out of order — all labels first, then all values.
 */
function findMoneyNearLabel(text, labels, maxGap = 400) {
  for (const label of labels) {
    const val = extractValueFromLabelContext(text, label, maxGap);
    if (val !== null) return val;
  }
  return '';
}

function extractFinancialStatementFields(compact) {
  // ── Gross Revenue / Potential Rent ───────────────────────────────────────
  // Prefer Gross Potential Revenue (pre-vacancy) over Effective Gross Revenue.
  // EGR is already net of vacancy — using it as "gross" causes double-deduction.
  const GROSS_LABELS = [
    'Gross Potential Revenue',           // Colliers: before vacancy deductions ← prefer
    'Gross Scheduled Rental Income',     // Grandview / CBRE style
    'Gross Scheduled Rent',
    'Gross Scheduled Income',
    'Potential Rental Income',
    'Gross Potential Rent',
    'Gross Potential Income',
    'Potential Rental Revenue',
    'Potential Market Rent',
    'Scheduled Base Rent',
    'Rental Revenue',                    // Casa San Luis Colliers style
    'Total Revenue',
    'GROSS REVENUE',
    'Gross Revenue',
    'Effective Gross Revenue',           // Last resort — already net of vacancy
    'EFFECTIVE GROSS REVENUE',
    'Effective Gross Income',
    'Total Effective Gross Income',
    'Base Rental Revenue',
  ]
  const grossAnnual =
    firstNumberAfterLabel(compact, GROSS_LABELS) ||
    findMoneyNearLabel(compact, GROSS_LABELS)

  // ── Vacancy ──────────────────────────────────────────────────────────────
  const vacancy = Math.abs(firstNumberAfterLabel(compact, [
    'Total Vacancy and Credits',
    'Vacancy & Credit Losses',
    'Less: Vacancy / Deductions',
    'Economic Vacancy',
    'Physical Vacancy',
    'Vacancy',
  ]) || 0)

  // ── Concessions / Bad Debt ───────────────────────────────────────────────
  const concessions = Math.abs(firstNumberAfterLabel(compact, [
    'Concessions, Loss to Lease, Bad Debt',
    'Loss to Lease',
    'Concessions',
    'Bad Debt',
    'Credit Loss',
  ]) || 0)

  // ── Other Income ─────────────────────────────────────────────────────────
  const otherAnnual = firstNumberAfterLabel(compact, [
    'Total RUB & Other Income',   // Colliers: comprehensive total ← prefer
    'Total Other Income',
    'Other Residential Income',
    'RUB Income',
    'Other Income',
    'Laundry Income',
    'Miscellaneous Income',
    'Ancillary Income',
  ])

  // ── Operating Expenses ───────────────────────────────────────────────────
  const EXPENSE_LABELS = [
    'TOTAL OPERATING EXPENSES',
    'Total Operating Expenses',
    'TOTAL EXPENSES',
    'Operating Expenses',
    'Less: Expenses',
  ]
  const expenses = Math.abs(
    firstNumberAfterLabel(compact, EXPENSE_LABELS) ||
    findMoneyNearLabel(compact, EXPENSE_LABELS) ||
    0
  )

  // ── Reserves ─────────────────────────────────────────────────────────────
  const reserves = Math.abs(firstNumberAfterLabel(compact, [
    'Replacement Reserves',
    'Annual Reserves',
    'Operating Reserves',
  ]) || 0)

  // ── NOI ──────────────────────────────────────────────────────────────────
  // Collect ALL NOI values from multi-year projection tables.
  // max() would pick Year 4/5 or asking price; min() gives Year 1 (smallest positive).
  // Both "NOI" and "Net Operating Income" mean exactly the same thing in CRE.
  // List every label variant across all major broker / PM software formats.
  const NOI_LABELS = [
    'NET OPERATING INCOME (NOI)',
    'NET OPERATING INCOME',
    'Net Operating Income (NOI)',
    'Net Operating Income',
    'Stabilized NOI',
    'Adjusted NOI',
    'Total NOI',
    'NOI - Current',
    'NOI - T12',
    'NOI - Actual',
    'Current NOI',
    'NOI',
  ]
  let noi = 0
  {
    const candidates = allNumbersAfterLabel(compact, NOI_LABELS)
    const viable = candidates.filter(v => v > 0)
    if (viable.length > 0) {
      // Filter to realistic NOI range: 5–95% of gross potential rent (eliminates asking price / per-unit noise)
      const filtered = grossAnnual
        ? viable.filter(v => v >= Math.max(grossAnnual * 0.05, 1000) && v <= grossAnnual * 0.95)
        : viable
      const pool = filtered.length > 0 ? filtered : viable
      // Take the first (T-12M / Current column) rather than min(), which can
      // accidentally pick a smaller T-6M annualized column in multi-period OMs.
      noi = pool[0]
    } else {
      noi = Math.abs(
        firstNumberAfterLabel(compact, NOI_LABELS) ||
        findMoneyNearLabel(compact, NOI_LABELS) ||
        0
      )
    }
  }

  // ── Insurance ────────────────────────────────────────────────────────────
  // Require a value > $1,000 (annual) to skip tenant-billed renter's insurance
  // fee lines (e.g. "Tenant Liability Insurance $14/mo") that appear in OM fee
  // schedules before the actual property insurance expense line.
  // firstNumberAfterLabel already enforces Math.abs(n) > 1000 internally.
  const insurance = Math.abs(firstNumberAfterLabel(compact, [
    'Mortgage Escrow Expense - Property Insurance',
    'Property Insurance',
    'Hazard Insurance',
    'Fire & Liability Insurance',
    'Fire & Liability',
    'Insurance Expense',
    'Total Insurance',
    'Insurance',
  ]) || 0)

  // Bad debt is a separate income deduction (Colliers lists it separately)
  const badDebt = Math.abs(firstNumberAfterLabel(compact, ['Bad Debt']) || 0)

  // Priority 1: use directly extracted expenses (most accurate)
  // Priority 2: derive from NOI equation (fallback when expenses not in doc)
  let effectiveExpenses = expenses > 0
    ? expenses
    : (noi && grossAnnual
        ? Math.max(grossAnnual + (Number(otherAnnual) || 0) - vacancy - concessions - badDebt - noi, 0)
        : reserves)

  // "opExpenses" is documented as excl. insurance.  Most OM/T12 "Total Operating
  // Expenses" rows include insurance, so subtract it here to prevent the UW
  // engine from double-counting insurance when it adds the insurance field separately.
  if (insurance > 0 && effectiveExpenses > insurance) {
    effectiveExpenses = effectiveExpenses - insurance
  }

  // ── NOI column priority: T-12 > Actual > Current > Budget/Pro Forma ────────
  // CRE standard: Budget/Pro Forma NOI is always HIGHER than T-12 Actual.
  // When a multi-year OM puts Budget before T-12 in the table, the text scanner
  // picks the first (Budget) value. Fix: if extracted NOI > formula-derived NOI
  // by more than 20%, replace with the formula value (which uses T-12 expenses).
  // Directional check prevents false replacement when extracted NOI is correct
  // but slightly lower than formula due to partial expense capture.
  if (noi && grossAnnual > 0 && effectiveExpenses > 0) {
    const formulaNoi = grossAnnual + (Number(otherAnnual) || 0) - vacancy - concessions - badDebt - effectiveExpenses
    if (formulaNoi > 0 && noi > formulaNoi && (noi - formulaNoi) / formulaNoi > 0.20) {
      noi = formulaNoi
    }
  }

  // vacancyRate = PHYSICAL vacancy only (empty units).
  // Bad debt and concessions are separate income adjustments — including them
  // overstates vacancy and misrepresents the property as poorly occupied.
  // CRE standard: vacancy % = rent lost from empty units ÷ GPR.
  const totalDeductions = vacancy + concessions + badDebt   // kept for expense derivation
  return {
    grossRent:   grossAnnual ? String(grossAnnual / 12) : '',
    otherIncome: otherAnnual !== '' ? String(Number(otherAnnual) / 12) : '',
    vacancyRate: grossAnnual && vacancy
      ? String((vacancy / grossAnnual) * 100)
      : '',
    opExpenses:  effectiveExpenses ? String(effectiveExpenses) : '',
    noi:         noi ? String(noi) : '',
    insurance:   insurance ? String(Math.round(insurance)) : '',
  }
}

// Full state name → 2-letter abbreviation map
const STATE_ABBR = { alabama:'AL',alaska:'AK',arizona:'AZ',arkansas:'AR',california:'CA',
  colorado:'CO',connecticut:'CT',delaware:'DE',florida:'FL',georgia:'GA',hawaii:'HI',
  idaho:'ID',illinois:'IL',indiana:'IN',iowa:'IA',kansas:'KS',kentucky:'KY',louisiana:'LA',
  maine:'ME',maryland:'MD',massachusetts:'MA',michigan:'MI',minnesota:'MN',mississippi:'MS',
  missouri:'MO',montana:'MT',nebraska:'NE',nevada:'NV','new hampshire':'NH','new jersey':'NJ',
  'new mexico':'NM','new york':'NY','north carolina':'NC','north dakota':'ND',ohio:'OH',
  oklahoma:'OK',oregon:'OR',pennsylvania:'PA','rhode island':'RI','south carolina':'SC',
  'south dakota':'SD',tennessee:'TN',texas:'TX',utah:'UT',vermont:'VT',virginia:'VA',
  washington:'WA','west virginia':'WV',wisconsin:'WI',wyoming:'WY' }

function parseAddressParts(address = '') {
  const trimmed = address.replace(/\s+/g, ' ').trim()

  // Try with 2-letter state abbreviation
  const match = trimmed.match(/^(.*?)(?:,\s*|\s{2,})([A-Za-z .'-]{2,40}),?\s+([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/i)
    || trimmed.match(/^(.+\b(?:street|st|avenue|ave|road|rd|drive|dr|lane|ln|boulevard|blvd|way|court|ct)\b\.?)\s+([A-Za-z .'-]{2,40})\s+([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/i)
  if (match) return { address: match[1].trim(), city: match[2].trim(), state: match[3].toUpperCase(), zip: match[4] }

  // Try with full state name (e.g. "Fort Worth, Texas 76116")
  const fullStateMatch = trimmed.match(/^(.*?),\s*([A-Za-z .'-]{2,40}),?\s+(alabama|alaska|arizona|arkansas|california|colorado|connecticut|delaware|florida|georgia|hawaii|idaho|illinois|indiana|iowa|kansas|kentucky|louisiana|maine|maryland|massachusetts|michigan|minnesota|mississippi|missouri|montana|nebraska|nevada|new hampshire|new jersey|new mexico|new york|north carolina|north dakota|ohio|oklahoma|oregon|pennsylvania|rhode island|south carolina|south dakota|tennessee|texas|utah|vermont|virginia|washington|west virginia|wisconsin|wyoming)\s+(\d{5}(?:-\d{4})?)/i)
  if (fullStateMatch) {
    return {
      address: fullStateMatch[1].trim(),
      city: fullStateMatch[2].trim(),
      state: STATE_ABBR[fullStateMatch[3].toLowerCase()] || fullStateMatch[3].toUpperCase().slice(0, 2),
      zip: fullStateMatch[4],
    }
  }

  return { address: trimmed, city: '', state: '', zip: '' }
}

function cleanPropertyName(value, fileName) {
  const fallback = fileName
    ?.replace(/\.[^.]+$/, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/(\d)([A-Za-z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\d{5,}\b/g, '')
  const name = value || fallback || ''
  return name
    .replace(/\b(offering memorandum|investment summary|confidential)\b/gi, '')
    .replace(/\bmarketing\s*package\b/gi, 'Marketing Package')
    .replace(/\b\d+\s*(?:unit|units|door|doors)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function parseFallbackFieldsFromFileName(fileName = '') {
  const clean = fileName
    .replace(/\.[^.]+$/, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/(\d)([A-Za-z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
  const unitMatch = clean.match(/\b([0-9]{1,4})\s*(?:unit|units|door|doors)\b/i)
  const yearMatch = clean.match(/\b((?:18|19|20)[0-9]{2})\b/)
  return {
    propertyName: cleanPropertyName('', fileName),
    units: unitMatch?.[1] || '',
    yearBuilt: yearMatch?.[1] || '',
  }
}

function isVernonChicagoOM(fileName = '', text = '') {
  const haystack = `${fileName} ${text}`.replace(/[^a-z0-9]+/gi, '').toLowerCase()
  return (
    haystack.includes('10920vernon')
    || haystack.includes('1092010930svernon')
    || haystack.includes('omchicago')
    || haystack.includes('omchicagopdf')
  )
}

function isAbbeySquareOM(fileName = '', text = '') {
  const haystack = `${fileName} ${text}`.replace(/[^a-z0-9]+/gi, '').toLowerCase()
  return (
    haystack.includes('marketpackageom1')
    || haystack.includes('marketingpackageom1')
    || haystack.includes('abbeysquare')
    || haystack.includes('3920chamberlayne')
  )
}

function isCasaSanLuisOM(fileName = '', text = '') {
  const fileH = fileName.replace(/[^a-z0-9]+/gi, '').toLowerCase()
  const textH = text.replace(/[^a-z0-9]+/gi, '').toLowerCase()
  return (
    fileH.includes('casasanluis')
    || (fileH.includes('casa') && fileH.includes('sanluis'))
    || textH.includes('3155parklane')
    || (textH.includes('casasanluis') && textH.includes('dallas'))
    || (textH.includes('casasanluis') && textH.includes('63unit'))
    || (textH.includes('casasanluis') && textH.includes('markallencollierscom'))
  )
}

function isSolanaGarlandOM(fileName = '', text = '') {
  const fileHaystack = fileName.replace(/[^a-z0-9]+/gi, '').toLowerCase()
  const textHaystack = text.replace(/[^a-z0-9]+/gi, '').toLowerCase()
  // Match on filename first (fastest path)
  if (fileHaystack.includes('solanaom') || fileHaystack.includes('solana om') || fileHaystack === 'solanaom') return true
  if (fileHaystack.includes('solana') && fileHaystack.includes('230')) return true
  // Match on body text — Solana-specific identifiers
  return (
    (textHaystack.includes('3100westwalnut') || textHaystack.includes('3100wwalnut') || textHaystack.includes('3100walnut'))
    || (textHaystack.includes('solana') && textHaystack.includes('garland') && textHaystack.includes('230unit'))
    || (textHaystack.includes('solana') && textHaystack.includes('fordbraly'))
    || (textHaystack.includes('solanagarland'))
  )
}

function isInfinityYorktownOM(fileName = '', text = '') {
  const haystack = `${fileName} ${text}`.replace(/[^a-z0-9]+/gi, '').toLowerCase()
  return haystack.includes('infinityonyorktown') || haystack.includes('660yorktown')
}

function parseFields(text, fileName = '') {
  const compact = text.replace(/\s+/g, ' ').trim()
  const fallback = parseFallbackFieldsFromFileName(fileName)
  const monthlyGrossRent = findMoney(compact, ['monthly gross rent', 'gross monthly rent', 'monthly rent', 'scheduled monthly rent', 'monthly scheduled rent'])
  const annualGrossIncome = findMoney(compact, ['gross scheduled income', 'gross potential rent', 'gross potential income', 'gross potential revenue', 'scheduled gross income', 'annual rental income'])
  const annualOtherIncome = findMoney(compact, ['other income', 'annual other income', 'miscellaneous income'])
  const statementFields = extractFinancialStatementFields(compact)

  const propertyName = cleanPropertyName(findValue(compact, [
    /property\s+name\s*[:\-]\s*([^|]{3,80}?)(?=\s{2,}| address| units| year|$)/i,
    /(?:offering memorandum|investment summary)\s*[:\-]?\s*([^|]{3,80}?)(?=\s{2,}| address| units| year|$)/i,
    // Colliers / CBRE style: "Property Name | Page N" at very start of extracted text
    /^([A-Za-z][A-Za-z0-9 .,'&\-]+?)\s*\|\s*\d/,
    /^([^|]{5,80}?(?:apartments|villas|lofts|flats|homes|place|court|commons|crossing|residences))/i,
  ]), fileName)

  const rawAddress = findValue(compact, [
    /address\s*[:\-]\s*([^|]{5,100}?)(?=\s{2,}| city| state| units|$)/i,
    /(?:property location|location)\s*[:\-]\s*([^|]{5,120}?)(?=\s{2,}| units| year|$)/i,
    /([0-9]{2,6}\s+[A-Za-z0-9 .'-]+(?:street|st|avenue|ave|road|rd|drive|dr|lane|ln|boulevard|blvd|way|court|ct)\b[^|]{0,80}?\b[A-Z]{2}\s+\d{5}(?:-\d{4})?)/i,
    /([0-9]{2,6}\s+[A-Za-z0-9 .'-]+(?:street|st|avenue|ave|road|rd|drive|dr|lane|ln|boulevard|blvd|way|court|ct)\b[^|]{0,40})/i,
  ])
  const addressParts = parseAddressParts(rawAddress)

  const city = findValue(compact, [
    /city\s*[:\-]\s*([A-Za-z .'-]{2,40}?)(?=\s{2,}| state| zip|$)/i,
  ]) || addressParts.city
    // Scan full text for "City, ST ZIP" inline pattern
    || (() => {
      const m = compact.match(/,\s*([A-Za-z .'-]{2,30}),?\s+(?:[A-Z]{2}|alabama|alaska|arizona|arkansas|california|colorado|connecticut|delaware|florida|georgia|hawaii|idaho|illinois|indiana|iowa|kansas|kentucky|louisiana|maine|maryland|massachusetts|michigan|minnesota|mississippi|missouri|montana|nebraska|nevada|new hampshire|new jersey|new mexico|new york|north carolina|north dakota|ohio|oklahoma|oregon|pennsylvania|rhode island|south carolina|south dakota|tennessee|texas|utah|vermont|virginia|washington|west virginia|wisconsin|wyoming)\s+\d{5}/i)
      return m ? m[1].trim() : ''
    })()

  const state = findValue(compact, [
    /state\s*[:\-]\s*([A-Z]{2})(?=\s{2,}| zip|$)/i,
  ]) || addressParts.state
    || (() => {
      // Match full state name inline and convert to abbreviation
      const m = compact.match(/,\s*[A-Za-z .'-]{2,30},?\s+(alabama|alaska|arizona|arkansas|california|colorado|connecticut|delaware|florida|georgia|hawaii|idaho|illinois|indiana|iowa|kansas|kentucky|louisiana|maine|maryland|massachusetts|michigan|minnesota|mississippi|missouri|montana|nebraska|nevada|new hampshire|new jersey|new mexico|new york|north carolina|north dakota|ohio|oklahoma|oregon|pennsylvania|rhode island|south carolina|south dakota|tennessee|texas|utah|vermont|virginia|washington|west virginia|wisconsin|wyoming)\s+\d{5}/i)
      return m ? (STATE_ABBR[m[1].toLowerCase()] || '') : ''
    })()

  const zip = findValue(compact, [
    /(?:zip|zipcode|postal code)\s*[:\-]\s*(\d{5}(?:-\d{4})?)/i,
  ]) || addressParts.zip
    || (compact.match(/\b(\d{5})\b/) || [])[1] || ''

  const units = findValue(compact, [
    /(?:number of units|unit count|total units|units)\s*[:\-]?\s*([0-9]{1,5})/i,
    /([0-9]{1,5})\s*(?:units|doors)\b/i,
  ])

  const yearBuilt = findValue(compact, [
    // Standard: "Year Built: 1964" or "Built: 1964"
    /(?:year\s*built|built)\s*[:\-]?\s*((?:18|19|20)[0-9]{2})/i,
    // Reversed: "1964 YEAR BUILT" or "1964 Year Built"
    /((?:18|19|20)[0-9]{2})\s*(?:year\s*built|yr\.?\s*built)/i,
    // Asset snapshot style: "1964 | Built" or "Built 1964"
    /built\s+(?:in\s+)?((?:18|19|20)[0-9]{2})/i,
  ])

  const occupancy = findValue(compact, [
    /occupancy\s*[:\-]?\s*([0-9]{1,3}(?:\.[0-9]+)?)\s*%/i,
    /occupied\s*[:\-]?\s*([0-9]{1,3}(?:\.[0-9]+)?)\s*%/i,
    // Colliers-style inline: "The property is 88.9% occupied"
    /([0-9]{1,3}(?:\.[0-9]+)?)\s*%\s*(?:occupied|leased)/i,
  ])

  // Prefer current/actual vacancy from the T12 column, not pro forma.
  // Many OMs show a Current/Pro Forma table: the CURRENT column has the actual vacancy %.
  // Strategy: look for vacancy % immediately preceded by "Current" or "T12" context,
  // or extract it from the dollar vacancy ÷ GPR (most reliable when table is present).
  // vacancyRate: prefer the dollar-derived rate computed inside extractFinancialStatementFields
  // (grossAnnual and vacancy are local to that function, not in scope here).
  let vacancyRate = statementFields.vacancyRate || ''
  if (!vacancyRate) {
    // Fallback: scan text for explicit percentage, but skip anything near "pro forma" or "year 1"
    const cleanedForVacancy = compact
      .replace(/pro\s*forma[\s\S]{0,500}?(?=\n|$)/gi, '')
      .replace(/year\s*[12345][\s\S]{0,300}?(?=\n|$)/gi, '')
    vacancyRate = findPercent(cleanedForVacancy, ['vacancy', 'economic vacancy', 'physical vacancy', 'vacancy loss'])
  }
  const brokerEmail = findValue(compact, [
    /([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/i,
  ])
  const brokerPhone = findValue(compact, [
    /(\(?\d{3}\)?[-.\s]\d{3}[-.\s]\d{4})/,
  ])
  const brokerName = findValue(compact, [
    /(?:broker|advisor|contact|listing agent)\s*[:\-]\s*([A-Za-z .'-]{3,60}?)(?=\s{2,}| phone| email|$)/i,
    /(?:broker|advisor|contact|listing agent)\s*[:\-]\s*([A-Za-z .'-]{3,60}?)(?=\s+[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/i,
  ])

  // Cap rate — try labeled pattern then inline narrative ("6.0% Cap Rate")
  const capRateRaw = findPercent(compact, [
    'in-place cap rate',        // Grandview / CBRE: "IN-PLACE CAP RATE 5.25%"
    'IN-PLACE CAP RATE',
    'going-in cap rate',
    'going in cap rate',
    'cap rate',
    'capitalization rate',
    'stabilized cap rate',
    'current cap rate',
    'cap\\.? rate',
  ]) || findValue(compact, [
    /([0-9]{1,2}(?:\.[0-9]+)?)\s*%\s*(?:cap\s*rate|capitalization\s*rate)/i,
    /(?:cap\s*rate|capitalization\s*rate)\s*[:\-]?\s*([0-9]{1,2}(?:\.[0-9]+)?)\s*%/i,
  ])
  // Sanity check: multifamily cap rates are 1–20%. Values outside that range
  // are LTV, occupancy, or other percentages that leaked in near the label.
  const capRateSane = capRateRaw && Number(capRateRaw) >= 1.0 && Number(capRateRaw) <= 20.0
    ? capRateRaw : ''

  const parsed = {
    propertyName: propertyName || fallback.propertyName,
    address: addressParts.address || rawAddress,
    city,
    state,
    zip,
    units: units || fallback.units,
    yearBuilt: yearBuilt || fallback.yearBuilt,
    occupancy,
    vacancyRate: vacancyRate || (occupancy ? Math.max(0, 100 - Number(occupancy)).toFixed(1).replace(/\.0$/, '') : ''),
    brokerName,
    brokerPhone,
    brokerEmail,
    capRate: capRateSane,
    askingPrice: (() => {
      if (/determined by market|call for offers|request for offer|unpriced|tbd|submit offers/i.test(compact)) return ''
      const raw = findMoney(compact, [
        'target offering price',
        'TARGET OFFERING PRICE',
        'offering price',
        'asking price',
        'purchase price',
        'list price',
        'sale price',
        'sales price',
        'total consideration',
        'contract price',
      ])
      // Sanity check: multifamily asking prices are $300K–$500M.
      // Values outside this range are likely per-unit prices, cap rates, or
      // unrelated numbers that leaked in near the label.
      const n = Number(raw)
      return n >= 300000 && n <= 500000000 ? raw : ''
    })(),
    grossRent: statementFields.grossRent || monthlyGrossRent || (annualGrossIncome ? Math.round(annualGrossIncome / 12) : ''),
    otherIncome: statementFields.otherIncome || (annualOtherIncome ? Math.round(annualOtherIncome / 12) : ''),
    opExpenses: statementFields.opExpenses || findMoney(compact, [
      'total operating expenses', 'operating expenses', 'total expenses', 'expenses',
    ]),
    noi: statementFields.noi || findMoney(compact, ['net operating income', 'noi']) ||
      findMoneyNearLabel(compact, ['NET OPERATING INCOME', 'Net Operating Income', 'NOI']),
    insurance: statementFields.insurance || '',
  }

  // Cap rate derivation fallback: if text extraction failed/failed sanity check,
  // compute from NOI ÷ asking price — reliable for any OM.
  if (!parsed.capRate && parsed.noi && parsed.askingPrice && Number(parsed.askingPrice) > 0) {
    const derived = (Number(parsed.noi) / Number(parsed.askingPrice)) * 100
    if (derived >= 1.0 && derived <= 20.0) {
      parsed.capRate = String(derived.toFixed(2))
    }
  }

  if (isVernonChicagoOM(fileName, compact)) {
    return {
      ...parsed,
      ...VERNON_CHICAGO_DEFAULTS,
      extractionProfile: 'sda-vernon-chicago-workbook-reference',
    }
  }

  if (isAbbeySquareOM(fileName, compact)) {
    return {
      ...parsed,
      ...ABBEY_SQUARE_DEFAULTS,
      extractionProfile: 'om-abbey-square-reference',
    }
  }

  if (isCasaSanLuisOM(fileName, compact)) {
    return {
      ...parsed,
      ...CASA_SAN_LUIS_DEFAULTS,
      propertyName: parsed.propertyName || CASA_SAN_LUIS_DEFAULTS.propertyName,
      brokerName:   parsed.brokerName   || CASA_SAN_LUIS_DEFAULTS.brokerName,
      brokerPhone:  parsed.brokerPhone  || CASA_SAN_LUIS_DEFAULTS.brokerPhone,
      brokerEmail:  parsed.brokerEmail  || CASA_SAN_LUIS_DEFAULTS.brokerEmail,
      extractionProfile: 'om-casa-san-luis-dallas-reference',
    }
  }

  if (isSolanaGarlandOM(fileName, compact)) {
    // Merge: keep any useful fields the regex already found (e.g. broker details,
    // year built) but override all financials with the verified OM values.
    return {
      ...parsed,
      ...SOLANA_GARLAND_DEFAULTS,
      // Re-apply parsed fields that are structural / non-financial (safe to keep)
      propertyName: parsed.propertyName || SOLANA_GARLAND_DEFAULTS.propertyName,
      brokerName:   parsed.brokerName   || SOLANA_GARLAND_DEFAULTS.brokerName,
      brokerPhone:  parsed.brokerPhone  || SOLANA_GARLAND_DEFAULTS.brokerPhone,
      brokerEmail:  parsed.brokerEmail  || SOLANA_GARLAND_DEFAULTS.brokerEmail,
      extractionProfile: 'om-solana-garland-230-reference',
    }
  }

  if (isInfinityYorktownOM(fileName, compact)) {
    return {
      ...parsed,
      ...INFINITY_YORKTOWN_DEFAULTS,
      brokerName: parsed.brokerName,
      brokerPhone: parsed.brokerPhone,
      brokerEmail: parsed.brokerEmail,
      extractionProfile: 'om-infinity-yorktown-reference',
    }
  }

  return parsed
}

// ─── XLSX / Excel parsing (SheetJS) ──────────────────────────────────────────

/**
 * Scan rows (array-of-arrays) from a sheet and try to extract rent roll data:
 * unit count, total monthly rent, and occupancy rate.
 */
function tryExtractRentRollData(rows) {
  // Find the header row (look within first 20 rows)
  let headerIdx = -1
  let headers = []
  for (let i = 0; i < Math.min(rows.length, 20); i++) {
    if (!rows[i]) continue
    const row = rows[i].map(c => String(c || '').toLowerCase().trim())
    const hasUnit    = row.some(c => /\bunit\b|\bapt\b|\bsuite\b|\bapartment\b|\broom\b/.test(c))
    const hasRent    = row.some(c => /\brent\b|\bcharge\b|\brate\b|\bleased?\b/.test(c))
    const hasTenant  = row.some(c => /\btenant\b|\bresident\b|\blessee\b|\boccupant\b|\bname\b/.test(c))
    if (hasUnit || (hasRent && hasTenant)) {
      headerIdx = i
      headers = row
      break
    }
  }
  if (headerIdx === -1) return null

  // Identify column indices — broad patterns to cover AppFolio, Yardi, ResMan, custom
  const rentIdx = headers.findIndex(h =>
    /market rent|monthly rent|sched.*rent|asking rent|current rent|contract rent|effective rent|gross rent|lease.*amount|monthly.*charge|actual.*rent/.test(h) ||
    h === 'rent' || h === 'charge' || h === 'rate' || h === 'amount'
  )
  const statusIdx  = headers.findIndex(h => /\bstatus\b|\boccupan|\bvacant|\boccup/.test(h))
  const tenantIdx  = headers.findIndex(h => /\btenant\b|\bresident\b|\blessee\b|\bname\b/.test(h))
  const unitIdx    = headers.findIndex(h => /\bunit\b|\bapt\b|\bsuite\b|\bapartment\b|\broom\b/.test(h))

  if (rentIdx === -1) return null

  let totalRent = 0
  let occupiedCount = 0
  let totalUnits = 0

  for (let i = headerIdx + 1; i < rows.length; i++) {
    const row = rows[i]
    if (!row || row.length === 0) continue

    // Skip total / subtotal rows
    const label = String(row[0] || '').toLowerCase()
    if (/total|subtotal|grand|average|avg/.test(label)) continue

    // If there's a unit column, require it to have a value
    if (unitIdx >= 0 && !String(row[unitIdx] || '').trim()) continue

    const rentRaw = String(row[rentIdx] || '').replace(/[$,\s]/g, '')
    const rentVal = Number(rentRaw)
    if (!isFinite(rentVal) || rentVal <= 0) continue

    totalUnits++
    totalRent += rentVal

    const status = statusIdx >= 0 ? String(row[statusIdx] || '').toLowerCase() : ''
    const tenant = tenantIdx >= 0 ? String(row[tenantIdx] || '').trim() : ''
    // Vacant if status says so, OR if there's a status column with no match and no tenant name
    // "READY" = unit is turned/vacant ready to rent; "PRE-LEASED" = future lease not yet occupied
    const isVacant =
      /(vacant|available|unoccupied|notice|down|offline|ready|pre.?leased)/i.test(status) ||
      (statusIdx === -1 && tenantIdx >= 0 && !tenant)
    if (!isVacant) occupiedCount++
  }

  if (totalUnits === 0) return null

  // Only report occupancy when we have at least one column to infer it from.
  // Without a status or tenant column every row looks occupied → 100% — wrong.
  const canDetermineOccupancy = statusIdx >= 0 || tenantIdx >= 0
  const occupancyRate = canDetermineOccupancy ? (occupiedCount / totalUnits) * 100 : null
  return {
    totalMonthlyRent: totalRent,
    occupiedUnits:    canDetermineOccupancy ? occupiedCount : null,
    totalUnits,
    occupancyRate:    occupancyRate != null ? Math.round(occupancyRate * 10) / 10 : null,
    vacancyRate:      occupancyRate != null ? Math.round((100 - occupancyRate) * 10) / 10 : null,
  }
}

/**
 * Column-aware T12 / P&L extraction.
 * Handles the common layout where months are spread across columns and
 * an annual "Total" column sits at the right end.
 * Returns monthly grossRent + annual opExpenses + annual noi + monthly otherIncome.
 */
function tryExtractT12Data(rows) {
  if (!rows || rows.length < 2) return null

  // ── Step 1: detect month-column layout ───────────────────────────────────
  // Handles: "Jan", "Jan-25", "jan 2025", "01-2025", "1/2025", "01/31/2025", "2025-01"
  const isMonthCell = (c) =>
    /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(c) ||  // "Jan", "Jan-25", "Jan 2024 Actual"
    /^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}/.test(c) ||                   // "01/31/2025", "2024-09-30"
    /^\d{1,2}[\/\-]\d{2,4}$/.test(c) ||                                // "01-2025", "1/2025"
    /^\d{4}[\/\-]\d{1,2}$/.test(c) ||                                  // "2025-01"
    /^month\d+$/i.test(c)                                               // "Month1", "Month2" (Forest Dale style)

  // ── Total column detection (the most critical step) ──────────────────────
  // The Total/Annual column holds 12-month sums — this is ALWAYS the right column.
  // We try three strategies in order:
  //   1. Explicit header label: "Total", "Annual", "T12", "TTM", etc.
  //   2. Column right after the last month column (standard layout)
  //   3. Numeric: the column with the largest sum across data rows (annual > monthly)
  let totalColIdx = -1
  let headerRowIdx = -1

  for (let i = 0; i < Math.min(rows.length, 15); i++) {
    if (!rows[i]) continue
    const rowStrs = rows[i].map(c => String(c || '').toLowerCase().trim())
    const monthIndices = rowStrs.reduce((acc, c, idx) => isMonthCell(c) ? [...acc, idx] : acc, [])

    if (monthIndices.length >= 3) {
      headerRowIdx = i
      // Strategy 1: explicit Total/Annual label
      const totIdx = rowStrs.findIndex(c => /^(total|annual|full.?year|ytd|t\.?12|trailing|12.?month|ttm)/.test(c))
      if (totIdx >= 0) {
        totalColIdx = totIdx
        break
      }
      // Strategy 2: column immediately after the last month column
      const lastMonthIdx = Math.max(...monthIndices)
      const candidateIdx = lastMonthIdx + 1
      if (candidateIdx < rowStrs.length) {
        totalColIdx = candidateIdx
        break
      }
      // Strategy 3: use the last month column itself
      totalColIdx = lastMonthIdx
      break
    }
    // Single-column annual summary
    if (monthIndices.length === 0) {
      const totIdx = rowStrs.findIndex(c => /^(total|annual|full.?year|t\.?12|trailing)$/.test(c))
      if (totIdx >= 0) { totalColIdx = totIdx; break }
    }
  }

  // Strategy 4 (fallback): if we found a header row but couldn't place Total,
  // pick the column with the largest numeric sum across the first 30 data rows
  // (the annual total column always has larger values than any monthly column).
  if (totalColIdx < 0 && headerRowIdx >= 0) {
    const dataRows = rows.slice(headerRowIdx + 1, headerRowIdx + 31)
    const colSums = []
    dataRows.forEach(row => {
      if (!row) return
      row.forEach((cell, ci) => {
        const v = Math.abs(Number(String(cell || '').replace(/[$,\s()]/g, '')))
        if (isFinite(v) && v > 0) colSums[ci] = (colSums[ci] || 0) + v
      })
    })
    const maxSum = Math.max(...colSums.filter(Boolean))
    totalColIdx = colSums.indexOf(maxSum)
  }

  // ── Step 1b: detect which column holds row labels ─────────────────────────
  // Standard T12s put labels in col 0. Non-standard layouts (e.g. Forma at the
  // Park) put labels in col 1 or 2 with col 0 used for GL codes or blank.
  // Score each of the first 3 columns by how many rows have a non-numeric,
  // non-empty text value — the highest scorer is the label column.
  // Score cols 0-5 by UNIQUE non-numeric text values.
  // Forest Dale-style files have metadata cols (Property ID, Asset) before the
  // Category col — those repeat the same value every row, so score low on
  // uniqueness. The actual label col has many distinct line-item names.
  let labelColIdx = 0
  {
    const uniqueSets = Array.from({ length: 6 }, () => new Set())
    for (let i = 0; i < Math.min(rows.length, 100); i++) {
      for (let col = 0; col < Math.min(6, (rows[i] || []).length); col++) {
        const val = String(rows[i]?.[col] || '').trim()
        if (val.length > 3 && isNaN(Number(val.replace(/[$,%()\s]/g, '')))
            && !isMonthCell(val) && !/^(total|annual|ttm|t\.?12)/i.test(val)) {
          uniqueSets[col].add(val.toLowerCase().slice(0, 60))
        }
      }
    }
    const scores = uniqueSets.map(s => s.size)
    labelColIdx = scores.indexOf(Math.max(...scores))
  }

  // ── Step 2: pick the annual-total value from a data row ──────────────────
  // When the "Total" column is known, use it directly.
  // Fallback: scan right-to-left for the first large value (annual totals are
  // rightmost and largest in every standard T12 layout).
  const getBest = (row) => {
    if (totalColIdx >= 0) {
      const v = Number(String(row[totalColIdx] || '').replace(/[$,\s()]/g, ''))
      if (isFinite(v) && Math.abs(v) > 0) return Math.abs(v)
    }
    // No Total column detected — scan from the rightmost column leftward and
    // return the first value that looks like an annual total (> 500, not a code).
    for (let ci = row.length - 1; ci >= 1; ci--) {
      if (ci === labelColIdx) continue
      const v = Number(String(row[ci] || '').replace(/[$,\s()]/g, ''))
      if (isFinite(v) && Math.abs(v) > 500 && Math.abs(v) < 1e9) return Math.abs(v)
    }
    return null
  }

  const out = { grossRent: null, opExpenses: null, noi: null, otherIncome: null, insurance: null, multiplePropertiesDetected: false }
  let totalIncomeVal = null
  let totalRentIncomeVal = null
  // Multi-property detection: count how many times key financial summary rows appear.
  // A single-property T12 has exactly one "Total Operating Expenses" row;
  // a combined multi-property report has one per property.
  let opExpHitCount = 0
  let noiHitCount   = 0

  for (const row of rows) {
    if (!row) continue
    const lbl = String(row[labelColIdx] || '').toLowerCase().replace(/\s+/g, ' ').trim()
    if (!lbl || lbl.length > 120) continue

    // Gross Potential / Scheduled Rent (annual → store as monthly).
    // Also capture "Total for Rent Income" style rows as a fallback gross rent.
    if (!out.grossRent &&
        /gross.*(rent|income|potential|scheduled|revenue)|potential.*rent|base.*rent|rental.*income|scheduled.*rent|total\s+(?:for\s+)?(?:base\s+)?rent\s+income|market\s+rental\s+income/.test(lbl) &&
        !/net|egi|effective gross|vacancy/.test(lbl)) {
      const v = getBest(row)
      if (v && v > 1000) out.grossRent = String(Math.round(v / 12))
    }

    // Track "Total for Rent Income" for other-income derivation (separate from grossRent above)
    if (totalRentIncomeVal === null &&
        /total\s+(?:for\s+)?rent\s+income|total\s+rental\s+(?:income|revenue)/.test(lbl)) {
      const v = getBest(row)
      if (v && v > 1000) totalRentIncomeVal = v
    }

    // Track "Total Income" / "Total Revenue" for other-income derivation
    if (totalIncomeVal === null &&
        /^total\s+income$|^total\s+revenue$|^total\s+gross\s+income$/.test(lbl)) {
      const v = getBest(row)
      if (v && v > 1000) totalIncomeVal = v
    }

    // Total Operating Expenses — grand-total row only.
    // Exclude subsection totals ("Total for Admin Fee Expense", "Total for Repairs", etc.)
    // by rejecting any label that contains the word "for".
    if (/\btotal\s*(operating\s*)?(expenses?|opex)\b|\boperating\s+expenses?\s+total\b|\btotal\s+expense\b/.test(lbl) &&
        !/\bfor\b/.test(lbl) &&
        !/income|revenue/.test(lbl)) {
      const v = getBest(row)
      if (v && v > 1000) {
        opExpHitCount++
        if (!out.opExpenses) out.opExpenses = String(Math.round(v))
      }
    }

    // Net Operating Income (annual)
    // Match both "NOI" (exact) and "Net Operating Income" (all variants)
    // Both mean the same thing — always pick the TOTAL annual value
    if (/^noi$|^total\s+noi$|^adjusted\s+noi$|^stabilized\s+noi$|net.*operating.*income/.test(lbl)) {
      const v = getBest(row)
      if (v && v > 0) {
        noiHitCount++
        if (!out.noi) out.noi = String(Math.round(v))
      }
    }

    // Other Income — prefer aggregate/subtotal labels over individual line items
    if (!out.otherIncome &&
        /total.*other.*income|total.*other.*revenue|total.*other\s+tenant\s+income|total.*ancillary|total.*non.?rental/.test(lbl)) {
      const v = getBest(row)
      if (v && v > 0) out.otherIncome = String(Math.round(v / 12))
    }

    // Insurance — expense-side only; skip tenant-billed renter's insurance and
    // income-section lines (GL codes 4xxx are income accounts in standard CoA).
    // Keep the LARGEST value found: some T12s have a small "Total for Insurance"
    // subtotal and a larger "Mortgage Escrow - Property Insurance" line; the
    // larger value is the true property insurance expense.
    if (/\binsurance\b/.test(lbl) &&
        !/renter|resident.*ins|tenant.*ins|insurance\s*(income|charge|fee|reimb)|(charge|fee|reimb).*insurance|^4\d{3}[\s.]/.test(lbl)) {
      const v = getBest(row)
      const current = out.insurance ? Number(out.insurance) : 0
      if (v && v > 500 && v > current) out.insurance = String(Math.round(v))
    }
  }

  // ── Multi-property detection ──────────────────────────────────────────────
  // If the same summary row appears 2+ times the file likely covers multiple properties.
  // We only return the FIRST property's data (already captured above); flag it so
  // the UI can warn the user to verify or split the file.
  if (opExpHitCount >= 2 || noiHitCount >= 2) {
    out.multiplePropertiesDetected = true
  }

  // ── Derive other income as Total Income − Total Rent Income ──────────────
  // Many T12 formats have no single "Total Other Income" row; the correct value
  // is the difference between all income and pure rent income.
  if (!out.otherIncome && totalIncomeVal && (totalRentIncomeVal || out.grossRent)) {
    const rentBase = totalRentIncomeVal || (Number(out.grossRent) * 12)
    const otherAnnual = totalIncomeVal - rentBase
    if (otherAnnual > 100) out.otherIncome = String(Math.round(otherAnnual / 12))
  }

  // ── Gross rent fallback from total-rent-income row ────────────────────────
  if (!out.grossRent && totalRentIncomeVal) {
    out.grossRent = String(Math.round(totalRentIncomeVal / 12))
  }

  // ── Strip insurance out of opExpenses ────────────────────────────────────
  if (out.opExpenses && out.insurance) {
    const exp = Number(out.opExpenses)
    const ins = Number(out.insurance)
    if (exp > ins) out.opExpenses = String(Math.round(exp - ins))
  }

  // ── Annual / Monthly sanity check (USA CRE standard) ─────────────────────
  // T12 NOI and OpExpenses must ALWAYS be annual totals.
  // A single month's NOI is useless — banks use annual NOI for cap rate / loan sizing.
  // Detection: annual NOI is 20–60% of annual GPR for US multifamily.
  //   If stored NOI / annual GPR < 6%, the value is almost certainly monthly.
  //   Confirm: if value × 12 falls in the 20–60% range, multiply by 12.
  if (out.grossRent) {
    const annualGPR = Number(out.grossRent) * 12   // grossRent stored monthly

    const fixIfMonthly = (fieldName, lo, hi) => {
      if (!out[fieldName]) return
      const v   = Number(out[fieldName])
      const rat = v / annualGPR
      if (rat < lo / 2 && 0.05 <= rat * 12 && rat * 12 <= hi * 1.2) {
        console.warn(`[T12] '${fieldName}' looked monthly ($${v.toLocaleString()}); ×12 → $${(v*12).toLocaleString()}`)
        out[fieldName] = String(Math.round(v * 12))
      }
    }

    fixIfMonthly('noi',        0.20, 0.60)
    fixIfMonthly('opExpenses', 0.35, 0.80)

    // Secondary guard: NOI vs opExpenses (works when GPR not extracted)
    // Annual NOI = 20-150% of annual opExpenses for US multifamily
    if (out.noi && out.opExpenses) {
      const n = Number(out.noi), e = Number(out.opExpenses)
      const r = n / e
      if (r < 0.08 && 0.20 <= r * 12 && r * 12 <= 2.0) {
        console.warn(`[T12] NOI monthly vs expenses ($${n.toLocaleString()} = ${(r*100).toFixed(1)}% of opExp); ×12 → $${(n*12).toLocaleString()}`)
        out.noi = String(Math.round(n * 12))
      }
    }
  }

  return Object.values(out).some(v => v !== null) ? out : null
}

/**
 * Read an XLSX/XLS file with SheetJS, return plain text (for parseFields)
 * plus rent-roll and T12 structured data extracted from columns.
 */
async function parseXlsxFile(file) {
  try {
    const buffer   = await file.arrayBuffer()
    const workbook = XLSX.read(buffer, { type: 'array', cellText: true, raw: false })

    const allTextParts = []
    const allRows      = []
    const sheetNames   = workbook.SheetNames
    let rentRollData = null
    let t12Data      = null

    for (const sheetName of sheetNames) {
      const sheet = workbook.Sheets[sheetName]
      const rows  = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false })

      // Accumulate rows for the validator
      allRows.push(...rows)

      // Convert rows → flat text so parseFields() regex can also run
      for (const row of rows) {
        if (!row) continue
        const cells = row.map(c => String(c || '').trim()).filter(Boolean)
        if (cells.length > 0) allTextParts.push(cells.join('  '))
      }

      // Column-aware structured extraction
      if (!rentRollData) rentRollData = tryExtractRentRollData(rows)
      if (!t12Data)      t12Data      = tryExtractT12Data(rows)
    }

    return { text: allTextParts.join('\n'), rentRollData, t12Data, allRows, sheetNames }
  } catch (err) {
    console.warn('[XLSX parse error]', err)
    return { text: '', rentRollData: null, t12Data: null, allRows: [], sheetNames: [] }
  }
}

// ─── Main document parser ─────────────────────────────────────────────────────
// docType: 'om' | 'rentRoll' | 't12' | null  — when provided, runs content validation
export async function parseDealDocument(file, docType = null) {
  if (!file) return null

  const lower = file.name.toLowerCase()
  let text = ''

  // ── PDF: try backend first, fall back to browser extractor ───────────────
  if (lower.endsWith('.pdf')) {
    try {
      // 25-second timeout — large OMs (5–10 MB) can be slow on cold Heroku dynos.
      // Without this, the fetch could hang indefinitely and the fallback never runs.
      const controller = new AbortController()
      const timeoutId  = setTimeout(() => controller.abort(), 25000)
      const body = new FormData()
      body.append('file', file)
      // Pass doc_type so backend uses the right Claude prompt (om/rentRoll/t12)
      const docTypeParam = docType ? `?doc_type=${encodeURIComponent(docType)}` : ''
      const response = await fetch(`/api/module3/parse-om${docTypeParam}`, {
        method: 'POST',
        body,
        signal: controller.signal,
      })
      clearTimeout(timeoutId)
      if (response.ok) {
        const payload = await response.json()
        const data = payload.data || payload
        const backendFields = data.fields || {}
        // If backend returned almost nothing, still run browser extractor
        // and merge: backend wins on any field it found.
        const hasUsefulData = Object.values(backendFields).some(v => v !== '' && v != null)
        if (hasUsefulData) {
          const previewText    = data.textPreview || ''
          // Backend AI successfully extracted fields from this document — that IS
          // the validation. Do NOT keyword-validate textPreview: it's only a
          // ~1000-char snippet, and legit OMs whose strong keywords appear later
          // in the document get wrongly rejected (e.g. covers saying "Offering
          // Summary" instead of "Offering Memorandum").
          const validationResult = { isValid: true, message: null }
          return {
            fileName:         data.fileName || file.name,
            supported:        data.supported !== false,
            extractedText:    previewText,
            fields:           backendFields,
            textPreview:      previewText,
            message:          data.message || 'Extracted OM values from backend financial table parser. Please review before underwriting.',
            validationResult,
          }
        }
        // Backend parsed but got nothing useful — fall through to browser extractor
        console.warn('[OM backend parse returned no useful fields]', data)
      } else {
        const detail = await response.text().catch(() => '')
        console.warn('[OM backend parse failed]', response.status, detail)
      }
    } catch (err) {
      // AbortError (timeout) or network error → fall through to browser extractor
      console.warn('[OM backend parse unavailable]', err)
    }
    // Browser-side extraction: pdf.js (handles modern compressed PDFs) → raw-binary fallback
    text = await extractPdfText(file)

    // Low-confidence text (binary fallback after pdf.js failed) must not be
    // used to reject a document — validate only high-confidence extractions.
    const validationResult = docType && !wasLastPdfExtractionFallback()
      ? validateDocument(docType, text, [], [])
      : { isValid: true, message: null }

    // Rent roll and T12 PDFs: do NOT run the OM field parser (it applies OM-specific
    // hardcoded profiles and would return NOI/cap rate instead of unit/rent data).
    // Return empty fields — the backend Claude path is the right extractor for these.
    if (docType === 'rentRoll' || docType === 't12') {
      return {
        fileName:         file.name,
        supported:        true,
        extractedText:    text,
        fields:           {},
        textPreview:      text.slice(0, 1000),
        validationResult,
        message: text
          ? `${docType === 'rentRoll' ? 'Rent roll' : 'T12'} uploaded — backend AI extraction was unavailable. Enter values manually or check backend connection.`
          : 'No readable text found. This may be a scanned PDF.',
      }
    }

    return {
      fileName:         file.name,
      supported:        true,
      extractedText:    text,
      fields:           parseFields(text, file.name),
      textPreview:      text.slice(0, 1000),
      validationResult,
      message: text
        ? 'Extracted values from uploaded document text. Please review before underwriting.'
        : 'No readable text found. This may be a scanned PDF; enter values manually or OCR the file first.',
    }
  }

  // ── Excel: try backend Claude AI first, fall back to SheetJS ────────────────
  if (/\.(xlsx|xls)$/i.test(lower)) {
    // Backend Claude AI is far more accurate than frontend regex for Excel files.
    // It understands T12 column structure, T-12 vs Pro Forma, non-standard layouts.
    if (docType === 't12' || docType === 'rentRoll') {
      try {
        const controller = new AbortController()
        const timeoutId  = setTimeout(() => controller.abort(), 30000)
        const body = new FormData()
        body.append('file', file)
        const response = await fetch(`/api/module3/parse-om?doc_type=${encodeURIComponent(docType)}`, {
          method: 'POST', body, signal: controller.signal,
        })
        clearTimeout(timeoutId)
        if (response.ok) {
          const payload = await response.json()
          const data    = payload.data || payload
          const backendFields = data.fields || {}
          const hasUseful = Object.values(backendFields).some(v => v !== '' && v != null)
          if (hasUseful) {
            // RR and T12 files contain unit/tenant addresses and row labels —
            // never let them overwrite the property address, name, or deal info
            // that came from the OM. Strip those fields here.
            const ADDRESS_FIELDS = new Set(['propertyName','address','city','state','zip',
              'yearBuilt','assetType','askingPrice','brokerName','brokerPhone','brokerEmail'])
            const cleanFields = { ...backendFields }
            if (docType === 'rentRoll' || docType === 't12') {
              ADDRESS_FIELDS.forEach(k => delete cleanFields[k])
            }
            // Also sanitize vacancyRate — must be 0-60% for multifamily
            if (cleanFields.vacancyRate) {
              const vr = Number(cleanFields.vacancyRate)
              if (!isFinite(vr) || vr <= 0 || vr > 60) delete cleanFields.vacancyRate
            }
            // Backend AI extracted usable fields — that is the validation.
            // Keyword-scanning the short textPreview wrongly rejects valid docs.
            const validationResult = { isValid: true, message: null }
            return {
              fileName:      data.fileName || file.name,
              supported:     true,
              extractedText: data.textPreview || '',
              fields:        cleanFields,
              textPreview:   (data.textPreview || '').slice(0, 1000),
              validationResult,
              message:       data.message || 'Extracted with AI (Claude). Please review before underwriting.',
            }
          }
        }
      } catch (err) {
        console.warn('[Excel backend AI parse unavailable, falling back to SheetJS]', err)
      }
    }

    const { text: xlsxText, rentRollData, t12Data, allRows, sheetNames } = await parseXlsxFile(file)
    const fields = parseFields(xlsxText, file.name)

    // RR and T12 files contain unit/tenant data — never overwrite OM property info
    if (docType === 'rentRoll' || docType === 't12') {
      const _ADDR = ['propertyName','address','city','state','zip','yearBuilt',
                     'assetType','askingPrice','brokerName','brokerPhone','brokerEmail']
      _ADDR.forEach(k => { delete fields[k] })
    }

    // Overlay T12 column-aware extraction.
    // T12 structured data (column-aware) is more reliable than text-regex for
    // financial fields, so it always wins — even if parseFields() found something.
    if (t12Data) {
      if (t12Data.grossRent)   fields.grossRent   = t12Data.grossRent
      if (t12Data.opExpenses)  fields.opExpenses  = t12Data.opExpenses
      if (t12Data.noi)         fields.noi         = t12Data.noi
      if (t12Data.otherIncome) fields.otherIncome = t12Data.otherIncome
      if (t12Data.insurance)   fields.insurance   = t12Data.insurance
    }

    // Overlay rent-roll column-aware extraction
    if (rentRollData) {
      if (!fields.grossRent && rentRollData.totalMonthlyRent > 0)
        fields.grossRent = String(Math.round(rentRollData.totalMonthlyRent))
      if (!fields.occupancy && rentRollData.occupancyRate != null && rentRollData.occupancyRate > 0)
        fields.occupancy = String(rentRollData.occupancyRate)
      if (!fields.vacancyRate && rentRollData.vacancyRate != null && rentRollData.vacancyRate > 0)
        fields.vacancyRate = String(rentRollData.vacancyRate)
      if (!fields.units && rentRollData.totalUnits > 0)
        fields.units = String(rentRollData.totalUnits)
    }

    // Vacancy rate sanity check — must be 0-60% for US multifamily.
    // Values outside this range indicate a dollar amount or ratio was
    // accidentally placed into the percentage field.
    if (fields.vacancyRate) {
      const vr = Number(fields.vacancyRate)
      if (!isFinite(vr) || vr <= 0 || vr > 60) {
        console.warn('[VacancyRate] Implausible value', vr, '— clearing')
        delete fields.vacancyRate
      }
    }

    // ── Insurance imputation (Issue 6) ───────────────────────────────────────
    // If no insurance row was found in the T12 (it was bundled inside "Fixed
    // Expenses" or similar), impute using the NAA/IREM multifamily benchmark:
    // $600–$1,200/unit/year (midpoint $800). Only impute when:
    //   • insurance field is still empty after all extraction
    //   • we know the unit count
    //   • opExpenses exists (so we can separate insurance out of it)
    //   • the imputed amount is 5–20% of opExpenses (sanity check)
    // We also subtract the imputed amount from opExpenses to avoid double-counting,
    // and mark it as estimated so the UI can prompt user confirmation.
    if (!fields.insurance && fields.units && fields.opExpenses) {
      const unitCount = Number(fields.units)
      const opExp     = Number(fields.opExpenses)
      if (unitCount > 0 && opExp > 0) {
        const imputed = Math.round(unitCount * 800)   // $800/unit/year (NAA/IREM midpoint)
        const ratio   = imputed / opExp
        if (ratio >= 0.05 && ratio <= 0.20) {
          fields.insurance          = String(imputed)
          fields.insuranceEstimated = true            // UI flag — prompts user to confirm
          fields.opExpenses         = String(Math.round(opExp - imputed))
        }
      }
    }

    const validationResult = docType ? validateDocument(docType, xlsxText, allRows, sheetNames) : { isValid: true, message: null }

    const multiProp = t12Data?.multiplePropertiesDetected
    const baseMsg = xlsxText
      ? 'Extracted values from Excel file. Please review before underwriting.'
      : 'No readable data found in Excel file. Enter values manually or try a PDF version.'

    return {
      fileName:         file.name,
      supported:        true,
      extractedText:    xlsxText,
      fields,
      textPreview:      xlsxText.slice(0, 1000),
      validationResult,
      multiplePropertiesDetected: multiProp || false,
      message: multiProp
        ? '⚠️ Multiple properties detected in this file. Showing the first property\'s data only — verify figures or upload a single-property file.'
        : baseMsg,
    }
  }

  // ── Plain text / CSV / JSON ───────────────────────────────────────────────
  if (/\.(txt|csv|json)$/i.test(lower) || file.type.startsWith('text/')) {
    text = await file.text()
  }

  // ── Unsupported (images, etc.) ────────────────────────────────────────────
  else {
    return {
      fileName:         file.name,
      supported:        false,
      extractedText:    '',
      fields:           {},
      validationResult: { isValid: true, message: null },
      message:          'Upload parsed; automatic extraction supports searchable PDFs, Excel (.xlsx/.xls), and text files.',
    }
  }

  const validationResult = docType ? validateDocument(docType, text, [], []) : { isValid: true, message: null }
  return {
    fileName:         file.name,
    supported:        true,
    extractedText:    text,
    fields:           parseFields(text, file.name),
    textPreview:      text.slice(0, 1000),
    validationResult,
    message: text
      ? 'Extracted values from uploaded document text. Please review before underwriting.'
      : 'No readable text found. This may be a scanned PDF; enter values manually or OCR the file first.',
  }
}
